/**
 * Pinia Store: Event Manager Registration Template
 *
 * A registration template is the list of questions an event's registration form asks on top
 * of the fixed ones (first name, last name, email, slot). Mirrors eventManagerCategory.
 */

import Vue from 'vue';
import { defineStore } from 'pinia';
import _ from 'lodash';
import uniqid from 'uniqid';

import RegistrationTemplateApi from '@/extensions/eventManager/store/api/registrationTemplate';
import { useSetupStore } from '@/stores';

// The field types the API accepts (RegistrationField::TYPES). Choice types carry options.
export const FIELD_TYPES = [
    { type: 'text', icon: 'font' },
    { type: 'textarea', icon: 'align-left' },
    { type: 'number', icon: 'hashtag' },
    { type: 'date', icon: 'calendar-alt' },
    { type: 'time', icon: 'clock' },
    { type: 'select', icon: 'caret-square-down' },
    { type: 'radio', icon: 'dot-circle' },
    { type: 'checkbox', icon: 'check-square' },
    { type: 'checkbox_group', icon: 'tasks' }
];

export const FIELD_TYPES_WITH_OPTIONS = ['select', 'radio', 'checkbox_group'];

// The fixed questions every registration form asks. They are rows of the template like the
// custom fields (so they can be ordered freely) but carry a `key`: they cannot be removed, keep
// their type, and their answers land on the booker / slot instead of a booking value.
// first name, last name, email and slot are always required; only the salutation may be optional.
// Default layout: salutation, first and last name share one row; the rest take a row each.
export const FIXED_FIELDS = [
    { key: 'salutation', type: 'select', label: 'Anrede', required: false, width: 'third', icon: 'user-tag' },
    { key: 'first_name', type: 'text', label: 'Vorname', required: true, width: 'third', icon: 'user' },
    { key: 'last_name', type: 'text', label: 'Nachname', required: true, width: 'third', icon: 'user' },
    { key: 'email', type: 'text', label: 'E-Mail', required: true, width: 'full', icon: 'envelope' },
    { key: 'phone', type: 'text', label: 'Telefon', required: false, width: 'full', icon: 'phone' },
    { key: 'slot', type: 'select', label: 'Termin', required: true, width: 'full', icon: 'calendar-alt' }
];

export const FIXED_ALWAYS_REQUIRED = ['first_name', 'last_name', 'email', 'slot'];

// Fixed fields a form may leave out; they can be removed and added back.
export const FIXED_OPTIONAL = ['salutation', 'phone'];

export const isRemovableField = field => !isFixedField(field) || FIXED_OPTIONAL.includes(field.key);

// How much of a row a field takes; neighbours flow into two- or three-column rows.
export const FIELD_WIDTHS = [
    { width: 'full', label: '1/1' },
    { width: 'half', label: '1/2' },
    { width: 'third', label: '1/3' }
];

export const isFixedField = field => !!_.get(field, 'key');

const createFixedField = (fixed, position) => ({
    id: `new:${uniqid()}`,
    key: fixed.key,
    type: fixed.type,
    label: fixed.label,
    placeholder: '',
    help_text: '',
    required: fixed.required,
    width: fixed.width || 'full',
    position,
    options: []
});

// A fresh template starts with the fixed questions in their canonical order.
const form = () => ({
    id: undefined,
    name: '',
    description: '',
    fields: FIXED_FIELDS.map((fixed, position) => createFixedField(fixed, position))
});

const rules = {
    name: 'required'
};

const ui = {
    form: {
        initialized: false,
        validateOnEnter: false
    }
};

const createField = type => ({
    id: `new:${uniqid()}`,
    key: null,
    type,
    label: '',
    placeholder: '',
    help_text: '',
    required: false,
    width: 'full',
    position: 0,
    options: FIELD_TYPES_WITH_OPTIONS.includes(type) ? [createOption()] : []
});

const createOption = () => ({
    id: `new:${uniqid()}`,
    label: '',
    value: '',
    position: 0
});

export const useEventManagerRegistrationTemplateStore = defineStore('eventManagerRegistrationTemplate', {
    state: () => ({
        loading: true,
        registrationTemplatesPaginated: {
            path: null,
            variables: {
                paginationSizes: [25, 50, 100]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: []
        },
        options: {},
        form: form(),
        rules: _.cloneDeep(rules),
        ui: _.cloneDeep(ui)
    }),

    getters: {
        formInitialized: state => state.ui.form.initialized,
        formValidateOnEnter: state => state.ui.form.validateOnEnter
    },

    actions: {
        addPaginatedRegistrationTemplatesToState(data) {
            this.registrationTemplatesPaginated.items = data.items;
            this.registrationTemplatesPaginated.pageInfo = _.cloneDeep(data.pageInfo);
            this.registrationTemplatesPaginated.allColumns = _.cloneDeep(data.config.allColumns);
            this.registrationTemplatesPaginated.link = _.cloneDeep(data.config.link);
            this.registrationTemplatesPaginated.showFilters = _.get(data.config, 'showFilters') || false;
            this.registrationTemplatesPaginated.defaultSort = _.get(data.config, 'defaultSort') || null;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            _.each(this.registrationTemplatesPaginated.allColumns, column => {
                let allowed = _.find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.registrationTemplatesPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.registrationTemplatesPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.registrationTemplatesPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.registrationTemplatesPaginated.filters, key);
            } else {
                Vue.set(this.registrationTemplatesPaginated.filters, key, value);
            }
        },

        defineSorting({ key, value }) {
            this.registrationTemplatesPaginated.variables.order = key;
            this.registrationTemplatesPaginated.variables.direction = value;
        },

        defineFilterVisibility(value) {
            this.registrationTemplatesPaginated.showFilters = value;
        },

        updateLoading(value) {
            this.loading = value;
        },

        updateRules(newRules) {
            this.rules = newRules;
        },

        resetRules() {
            this.rules = _.cloneDeep(rules);
        },

        resetUI() {
            this.ui = _.cloneDeep(ui);
        },

        insertRegistrationTemplate(template) {
            this.form = template;
        },

        // Form field setters
        setFormInitialized(flag) {
            this.ui.form.initialized = flag;
        },

        setFormValidateOnEnter(flag) {
            this.ui.form.validateOnEnter = flag;
        },

        setFormName(value) {
            this.form.name = value;
        },

        setFormDescription(value) {
            this.form.description = value;
        },

        // Fields

        addField(type) {
            if (!this.form.fields) Vue.set(this.form, 'fields', []);
            const field = createField(type);
            field.position = this.form.fields.length;
            this.form.fields.push(field);
            return field;
        },

        removeField(index) {
            // The mandatory fixed questions are part of every form.
            if (!isRemovableField(this.form.fields[index])) return;
            this.form.fields.splice(index, 1);
            this.renumberFields();
        },

        // Brings back an optional fixed field (salutation, phone) that was removed.
        addFixedField(key) {
            const fixed = _.find(FIXED_FIELDS, { key });
            if (!fixed || !FIXED_OPTIONAL.includes(key)) return;
            if (_.find(this.form.fields, { key })) return;
            if (!this.form.fields) Vue.set(this.form, 'fields', []);
            this.form.fields.push(createFixedField(fixed, this.form.fields.length));
        },

        moveField(index, direction) {
            const target = index + direction;
            if (target < 0 || target >= this.form.fields.length) return;
            const fields = this.form.fields;
            const [field] = fields.splice(index, 1);
            fields.splice(target, 0, field);
            this.renumberFields();
        },

        // The drag-and-drop list hands back the complete new order.
        reorderFields(fields) {
            Vue.set(this.form, 'fields', fields);
            this.renumberFields();
        },

        renumberFields() {
            this.form.fields.forEach((field, position) => Vue.set(field, 'position', position));
        },

        setFieldValue({ index, key, value }) {
            const field = this.form.fields[index];
            if (!field) return;

            // A fixed field keeps its type, and name / email / slot stay required.
            if (isFixedField(field)) {
                if (key === 'type') return;
                if (key === 'required' && FIXED_ALWAYS_REQUIRED.includes(field.key)) return;
            }

            Vue.set(field, key, value);

            // Switching to / from a choice type: make sure the option list fits the type.
            if (key === 'type') {
                if (FIELD_TYPES_WITH_OPTIONS.includes(value)) {
                    if (!field.options || !field.options.length) Vue.set(field, 'options', [createOption()]);
                } else {
                    Vue.set(field, 'options', []);
                }
            }
        },

        addOption(fieldIndex) {
            const field = this.form.fields[fieldIndex];
            if (!field) return;
            if (!field.options) Vue.set(field, 'options', []);
            const option = createOption();
            option.position = field.options.length;
            field.options.push(option);
        },

        removeOption({ fieldIndex, optionIndex }) {
            const field = this.form.fields[fieldIndex];
            if (!field || !field.options) return;
            field.options.splice(optionIndex, 1);
            field.options.forEach((option, position) => Vue.set(option, 'position', position));
        },

        setOptionLabel({ fieldIndex, optionIndex, value }) {
            const option = _.get(this.form.fields, [fieldIndex, 'options', optionIndex]);
            if (!option) return;
            Vue.set(option, 'label', value);
        },

        // Async actions
        fetchRegistrationTemplatesPaginated() {
            return RegistrationTemplateApi.fetchRegistrationTemplatesPaginated(this.registrationTemplatesPaginated).then(data => {
                this.addPaginatedRegistrationTemplatesToState(data);

                const activeColumnKeys = _.map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        fetchRegistrationTemplate(id) {
            return RegistrationTemplateApi.fetchRegistrationTemplate(id).then(template => {
                this.insertRegistrationTemplate(_.cloneDeep(template));
            });
        },

        create(template) {
            const setupStore = useSetupStore();
            const cms = setupStore.activeCms;
            template.cms = { id: cms.id };

            return RegistrationTemplateApi.create(template).then(createdTemplate => {
                this.insertRegistrationTemplate(_.cloneDeep(createdTemplate));
                this.resetRules();
                return createdTemplate;
            });
        },

        update(template) {
            return RegistrationTemplateApi.update(template).then(updatedTemplate => {
                this.insertRegistrationTemplate(_.cloneDeep(updatedTemplate));
                this.resetRules();
                return updatedTemplate;
            });
        },

        deleteRegistrationTemplate(id) {
            return RegistrationTemplateApi.delete(id).then(() => {
                this.reset();
                this.resetRules();
                this.resetUI();
            });
        },

        reset() {
            this.insertRegistrationTemplate(form());
        }
    }
});
